import type { Tone } from '../tone';
import { labDaysBefore } from './labDay';

/**
 * Where a due date stands against today: the one definition every screen,
 * count and filter reads. The request of 7 October 2026, section 6.
 *
 * "Overdue" had come to mean three things. A case's own status said it only
 * once the date had passed. The lists, the figure above a stage and the
 * Overdue only filters said it a working day early, as a warning. And the
 * Overview's attention list said it on the day itself. A case due on Sunday
 * read Overdue from Thursday, because Friday and Saturday are not working days
 * and the lists counted working days. The lab asked why a case that was not
 * late said it was.
 *
 * So: overdue means the due date is BEFORE today, on the lab's calendar, and
 * nothing else. Due today and due tomorrow are their own two words, in amber.
 * Anything later is the date and no colour. The comparison is of calendar
 * days, never of working days: "tomorrow" is the next day on the wall, weekend
 * or not. `daysLeft` on a case still counts working days and still says how
 * long the bench has; it no longer decides a word or a colour.
 *
 * Both arguments are lab days, `YYYY-MM-DD`, which compare as text.
 */
export const DUE_KEYS = ['overdue', 'today', 'tomorrow', 'later'] as const;
export type DueKey = (typeof DUE_KEYS)[number];

export function dueKey(deliveryDate: string, today: string): DueKey {
  if (deliveryDate < today) return 'overdue';
  if (deliveryDate === today) return 'today';
  if (deliveryDate === labDaysBefore(today, -1)) return 'tomorrow';
  return 'later';
}

/**
 * The tone of each: red for late, amber for today and tomorrow, none after.
 *
 * `late` is the red of this system. It was kept for urgent alone, on the
 * reasoning that a lab always has several late cases and red on all of them
 * drowns the urgent one. With overdue now meaning really late, the lab asked
 * for it in red, and a case that is late is the thing red is for.
 */
export const DUE_TONE: Record<DueKey, Tone> = {
  overdue: 'late',
  today: 'watch',
  tomorrow: 'watch',
  later: 'ok',
};

/** The three that have a word. A later date is shown as the date itself. */
export const DUE_LABEL: Record<Exclude<DueKey, 'later'>, string> = {
  overdue: 'Overdue',
  today: 'Due today',
  tomorrow: 'Due tomorrow',
};

/**
 * Both words, for a case that is urgent and late at once.
 *
 * Urgent used to win the Status column outright, so the Overdue filter listed
 * rows that did not say Overdue, and the lab read them as rows that should not
 * be there. Neither word hides the other now. The request of 8 October 2026.
 */
export const URGENT_OVERDUE_LABEL = `Urgent · ${DUE_LABEL.overdue}`;

/**
 * Whether a case is late: its date has passed and it is not finished.
 *
 * A finished case is never late, whatever the date says: one whose trays are
 * all Ready, one that has been shipped or received, one that was archived.
 * What "finished" is for a tray or an order is the caller's to say, because a
 * tray knows its bench and an order knows its state.
 */
export function isLate(deliveryDate: string, today: string, finished: boolean): boolean {
  return !finished && deliveryDate < today;
}

/**
 * How a case's date stands, for the line under its number: late by so many
 * days, due today, or due in so many. Nothing for a finished case, whose date
 * counts down to nothing.
 *
 * The line said "due in" and the working days left, whatever they were: "due
 * in -3 days" for a late case, "due in 1 days", and "due in 4 days" for a case
 * already Ready. Late and today are decided by `dueKey`, the rule of the
 * Status column, so the line and the column never disagree. The days are the
 * server's working days left, which say how long the bench has: one at the
 * least, because a case can be a day late with no working day gone, over a
 * weekend, and a date still ahead is never "in 0 days". The lab's answer of
 * 8 October 2026.
 */
export type DueCountdown =
  | { kind: 'overdue'; days: number }
  | { kind: 'today' }
  | { kind: 'in'; days: number };

export function dueCountdown(
  deliveryDate: string,
  today: string,
  daysLeft: number,
  finished: boolean,
): DueCountdown | null {
  if (finished) return null;
  const key = dueKey(deliveryDate, today);
  if (key === 'overdue') return { kind: 'overdue', days: Math.max(1, Math.abs(daysLeft)) };
  if (key === 'today') return { kind: 'today' };
  return { kind: 'in', days: Math.max(1, daysLeft) };
}
